import { ref, type Ref } from "vue";
import "./usePointer.css";

/**
 * Unified pointer composable handling tap, hover, and drag.
 *
 * Usage:
 *   const pointer = usePointer<Tile>(
 *       (tile) => activate(tile),
 *       (from, to) => move(from, to),
 *   )
 *
 *   watch(pointer.hovered, (tile) => updatePreview(tile))
 *   watch(pointer.dragging, (tile) => updateDragState(tile))
 *
 *   // Template - on container/target elements:
 *   v-bind="pointer.target(tile, tile.key)"
 *
 *   // Template - on draggable elements inside:
 *   v-bind="pointer.source(tile)"
 */
export function usePointer<T>(
  onTap: (item: T) => void,
  onDrop: (from: T, to: T) => void,
) {
  const LONG_PRESS_MS = 300;
  const DRAG_THRESHOLD = 10;

  // Reactive state
  const hovered: Ref<T | undefined> = ref();
  const dragging: Ref<T | undefined> = ref();
  const dragOver: Ref<T | undefined> = ref();

  // Internal state
  const targetMap = new Map<string, T>();
  let draggedEl: HTMLElement | null = null;

  // Touch/pointer state
  let activePointerId: number | null = null;
  let pointerStartPos: { x: number; y: number } | null = null;
  let pointerStartTime: number = 0;
  let mouseDownTime: number = 0;
  let longPressTimer: ReturnType<typeof setTimeout> | null = null;
  let isLongPress = false;
  let isDragging = false;
  let currentSourceItem: T | undefined = undefined;

  // ─────────────────────────────────────────────────────────────
  // Hover
  // ─────────────────────────────────────────────────────────────

  function setHovered(item: T | undefined) {
    if (hovered.value === item) return;
    hovered.value = item;
  }

  function findTargetAt(x: number, y: number): T | undefined {
    const elements = document.elementsFromPoint(x, y);
    for (const el of elements) {
      if (el === draggedEl || draggedEl?.contains(el)) continue;
      const targetEl = el.closest("[data-pointer-target]") as HTMLElement | null;
      if (targetEl) {
        const key = targetEl.getAttribute("data-pointer-target");
        if (key) return targetMap.get(key);
      }
    }
    return undefined;
  }

  // ─────────────────────────────────────────────────────────────
  // Drag (HTML5 API for mouse)
  // ─────────────────────────────────────────────────────────────

  const DRAG_CLASS = "use-pointer-dragging";

  function startDrag(el: HTMLElement, clientX: number, clientY: number, item: T) {
    isDragging = true;
    dragging.value = item;
    draggedEl = el;

    const rect = el.getBoundingClientRect();
    el.style.setProperty("--drag-w", rect.width + "px");
    el.style.setProperty("--drag-h", rect.height + "px");
    const offsetX = ((clientX - rect.left) / rect.width) * 100;
    const offsetY = ((clientY - rect.top) / rect.height) * 100;
    el.style.setProperty("--drag-offset-x", offsetX + "%");
    el.style.setProperty("--drag-offset-y", offsetY + "%");
    el.style.setProperty("--drag-x", clientX + "px");
    el.style.setProperty("--drag-y", clientY + "px");
  }

  function moveDrag(clientX: number, clientY: number) {
    if (!draggedEl) return;
    if (!draggedEl.classList.contains(DRAG_CLASS)) {
      draggedEl.classList.add(DRAG_CLASS);
    }
    if (clientX || clientY) {
      draggedEl.style.setProperty("--drag-x", clientX + "px");
      draggedEl.style.setProperty("--drag-y", clientY + "px");
    }
  }

  function endDrag() {
    if (draggedEl) {
      draggedEl.classList.remove(DRAG_CLASS);
      draggedEl.style.removeProperty("--drag-x");
      draggedEl.style.removeProperty("--drag-y");
      draggedEl.style.removeProperty("--drag-w");
      draggedEl.style.removeProperty("--drag-h");
      draggedEl.style.removeProperty("--drag-offset-x");
      draggedEl.style.removeProperty("--drag-offset-y");
    }
    dragging.value = undefined;
    dragOver.value = undefined;
    draggedEl = null;
    isDragging = false;
  }

  // ─────────────────────────────────────────────────────────────
  // Pointer events (touch support)
  // ─────────────────────────────────────────────────────────────

  function clearLongPressTimer() {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      longPressTimer = null;
    }
  }

  function cleanupPointer() {
    clearLongPressTimer();
    document.removeEventListener("pointermove", onGlobalPointerMove);
    document.removeEventListener("pointerup", onGlobalPointerUp);
    document.removeEventListener("pointercancel", onGlobalPointerUp);
    pointerStartPos = null;
    activePointerId = null;
    isLongPress = false;
    currentSourceItem = undefined;
  }

  function onGlobalPointerMove(e: PointerEvent) {
    if (e.pointerId !== activePointerId || !pointerStartPos) return;

    const dx = e.clientX - pointerStartPos.x;
    const dy = e.clientY - pointerStartPos.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance > DRAG_THRESHOLD) {
      clearLongPressTimer();

      if (!isDragging && currentSourceItem !== undefined) {
        // Start drag
        const el = document.querySelector(`[data-pointer-source-active]`) as HTMLElement;
        if (el) {
          el.removeAttribute("data-pointer-source-active");
          startDrag(el, e.clientX, e.clientY, currentSourceItem);
        }
        setHovered(undefined);
      }

      if (isDragging) {
        moveDrag(e.clientX, e.clientY);
        const target = findTargetAt(e.clientX, e.clientY);
        dragOver.value = target;
      }
    }
  }

  function onGlobalPointerUp(e: PointerEvent) {
    if (e.pointerId !== activePointerId) return;

    const wasDragging = isDragging;
    const wasLongPress = isLongPress;
    const item = currentSourceItem;

    if (wasDragging) {
      // Complete drop
      const target = findTargetAt(e.clientX, e.clientY);
      if (target !== undefined && dragging.value !== undefined) {
        onDrop(dragging.value, target);
      }
      endDrag();
    } else if (wasLongPress) {
      // Long press ended - just clear hover, no tap
      setHovered(undefined);
    } else if (item !== undefined) {
      // Quick tap - trigger tap callback
      onTap(item);
    }

    // Clean up active marker
    const el = document.querySelector(`[data-pointer-source-active]`);
    el?.removeAttribute("data-pointer-source-active");

    cleanupPointer();
  }

  // ─────────────────────────────────────────────────────────────
  // Public API
  // ─────────────────────────────────────────────────────────────

  return {
    /** Currently hovered item (mouse hover or touch long-press) */
    hovered,
    /** Currently dragging item */
    dragging,
    /** Current drop target while dragging */
    dragOver,

    /**
     * Bind to drop target elements (also handles hover)
     */
    target: (item: T, key: string) => ({
      "data-pointer-target": key,

      // Mouse hover
      onMouseenter() {
        if (!isDragging) setHovered(item);
      },
      onMouseleave() {
        if (!isDragging) setHovered(undefined);
      },

      // HTML5 drag target
      onDragover(e: DragEvent) {
        e.preventDefault();
        dragOver.value = item;
      },
      onDrop() {
        if (dragging.value !== undefined) {
          onDrop(dragging.value, item);
        }
        endDrag();
      },
    }),

    /**
     * Bind to draggable source elements (also handles tap)
     */
    source: (item: T) => ({
      draggable: true,

      // HTML5 drag (mouse)
      onDragstart(e: DragEvent) {
        const el = e.currentTarget as HTMLElement;
        startDrag(el, e.clientX, e.clientY, item);

        // Hide native drag image
        const empty = document.createElement("div");
        empty.style.opacity = "0";
        document.body.appendChild(empty);
        e.dataTransfer?.setDragImage(empty, 0, 0);
        requestAnimationFrame(() => empty.remove());
      },
      onDrag(e: DragEvent) {
        moveDrag(e.clientX, e.clientY);
      },
      onDragend() {
        endDrag();
      },

      // Mouse click (for non-drag clicks)
      onMousedown() {
        mouseDownTime = Date.now();
      },
      onClick(e: MouseEvent) {
        // Only handle if not touch (touch handled by pointer events)
        if (e.detail === 0) return; // Synthetic click from touch
        if (Date.now() - mouseDownTime >= LONG_PRESS_MS) return;
        onTap(item);
      },

      // Touch: tap, long-press hover, or drag
      onPointerdown(e: PointerEvent) {
        if (e.pointerType !== "touch") return;

        e.preventDefault();
        activePointerId = e.pointerId;
        pointerStartPos = { x: e.clientX, y: e.clientY };
        pointerStartTime = Date.now();
        isLongPress = false;
        isDragging = false;
        currentSourceItem = item;

        // Mark element for later reference
        const el = e.currentTarget as HTMLElement;
        el.setAttribute("data-pointer-source-active", "");
        el.setPointerCapture(e.pointerId);

        // Start long-press timer for hover preview
        longPressTimer = setTimeout(() => {
          isLongPress = true;
          setHovered(item);
        }, LONG_PRESS_MS);

        document.addEventListener("pointermove", onGlobalPointerMove);
        document.addEventListener("pointerup", onGlobalPointerUp);
        document.addEventListener("pointercancel", onGlobalPointerUp);
      },
    }),

    /** Register target by key for touch hit testing */
    register(key: string, item: T) {
      targetMap.set(key, item);
    },

    /** Unregister target */
    unregister(key: string) {
      targetMap.delete(key);
    },

    /** Clear all state */
    clear() {
      setHovered(undefined);
      endDrag();
      cleanupPointer();
    },
  };
}
